/**
 * Inject a third-party <script> at runtime.
 *
 * Runtime injection rather than a tag in `public/index.html` is deliberate:
 * `docker-entrypoint.sh` rewrites `%%TOKEN%%` placeholders in `dist/*.js` and
 * `dist/*.json` only, so a key written into `index.html` is frozen at image
 * build time and cannot be configured -- or withheld -- per deployment.
 *
 * Resolves when the script loads, rejects if it fails. Callers are expected to
 * swallow the rejection: a dead vendor must never break the app.
 */
export const loadScript = (options) => new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
        reject(new Error('loadScript called without a document'));
        return;
    }
    if (document.getElementById(options.id)) {
        resolve();
        return;
    }
    const script = document.createElement('script');
    script.id = options.id;
    script.src = options.src;
    if (options.async)
        script.async = true;
    if (options.defer)
        script.defer = true;
    if (options.crossOrigin)
        script.crossOrigin = options.crossOrigin;
    if (options.data) {
        Object.keys(options.data).forEach((key) => {
            script.setAttribute(`data-${key}`, options.data[key]);
        });
    }
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load third-party script: ${options.id}`));
    document.head.appendChild(script);
});
